import { round2 } from '../tabs/forecasting/forecastingModel';
import { INCLUSION_TYPE_OPTIONS } from '../tabs/inclusions/fields';

import {
  blankedErrorNote,
  blankedPriceNote,
  cellAddress,
  cellAt,
  findImportColumns,
  positiveNumberOf,
  priceOf,
  textOf,
  warnAt,
} from './sheetCells';
import type { ParsedInclusion, SheetContext } from './types';

const COLUMNS = {
  name: 'Asset Name',
  type: 'Type',
  description: 'Inclusion Description',
  prv: 'PRV',
  rrp: 'RRP',
} as const;

// The template fills untyped cells with formulas that show 0.
function typedTextOf(value: unknown): string | null {
  const text = textOf(value)?.replace(/\s+/g, ' ');
  return text && text !== '0' ? text : null;
}

function typeOf(value: unknown): string | null {
  const text = typedTextOf(value)?.toLowerCase();
  return INCLUSION_TYPE_OPTIONS.find((type) => type.toLowerCase() === text) ?? null;
}

export function parseInclusions(context: SheetContext): Array<ParsedInclusion> {
  const columns = findImportColumns(context, COLUMNS, 'inclusions', 'inclusions');
  if (!columns) {
    return [];
  }

  const inclusions: Array<ParsedInclusion> = [];
  for (let row = 1; row < context.rows.length; row++) {
    const cell = (column: keyof typeof COLUMNS) => cellAt(context.rows, row, columns[column]);
    const name = typedTextOf(cell('name'));
    const prv = priceOf(cell('prv'));
    // RRP goes to Salesforce and its field takes cents, unlike PRV, which the tool shows in whole dollars.
    const rrp = positiveNumberOf(cell('rrp'));
    const reject = (message: string) =>
      context.issues.push({
        section: 'inclusions',
        severity: 'error',
        cell: cellAddress(row, columns.name),
        row: row + 1,
        message,
      });

    if (!name) {
      if (prv !== null || rrp !== null) {
        reject('the Asset Name is empty. Name the inclusion in the workbook and upload again.');
      }
      continue;
    }
    if (name.startsWith('#')) {
      reject(`the Asset Name shows ${name.replace(/^#ERROR_/, '')}. Fix the workbook and upload again.`);
      continue;
    }

    const where = (column: keyof typeof COLUMNS) => ({ column: COLUMNS[column], row: row + 1, tab: 'Inclusions' });
    const warn = (column: keyof typeof COLUMNS, note: string | null) =>
      warnAt(context, 'inclusions', row, columns[column], note);
    warn('prv', blankedPriceNote(cell('prv'), prv, where('prv')));
    warn('rrp', blankedPriceNote(cell('rrp'), rrp, where('rrp')));
    const descriptionBlanked = warn('description', blankedErrorNote(cell('description'), where('description')));
    inclusions.push({
      row: row + 1,
      name,
      type: typeOf(cell('type')),
      description: descriptionBlanked ? null : typedTextOf(cell('description')),
      prv,
      rrp: rrp === null ? null : round2(rrp),
    });
  }
  return inclusions;
}
